import React from "react";
import Hls from "hls.js";
import { authorizedFetch } from "./boot.js";
import { Card } from "./ui.jsx";
import { ActivityDate, ParadeActivity, useParadeActivity } from "./parade-activity.jsx";
import { cameraDiagnostics, mediaState } from "./camera-diagnostics.js";

const { useEffect, useRef, useState } = React;
const MAX_LIVE_STREAMS = 2;

async function cameraRequest(ref, params, signal) {
  for (let attempt = 0; ; attempt++) {
    const response = await authorizedFetch(`/collections/${ref}/?${new URLSearchParams(params)}`, { credentials: "include", signal });
    if (response.ok) return response.json();
    if (attempt < 2 && (response.status === 429 || response.status >= 500)) {
      await new Promise((resolve, reject) => {
        const abort = () => { clearTimeout(timer); reject(new DOMException("Camera request aborted", "AbortError")); };
        const timer = setTimeout(() => { signal.removeEventListener("abort", abort); resolve(); }, 300 * 2 ** attempt);
        signal.addEventListener("abort", abort, { once: true });
        if (signal.aborted) abort();
      });
      continue;
    }
    const error = new Error(response.status === 404 ? "Camera access is no longer available." : response.status === 401 ? "Sign in again to view cameras." : "Camera access could not be refreshed. Please retry.");
    error.status = response.status;
    throw error;
  }
}

// These reads enrich labels only. The camera catalogue and each playback grant
// remain the authority for which images this realm may see.
async function cameraLots(sale, admin, signal, publish) {
  const vendors = [];
  if (admin) {
    for (let offset = 0; ; offset += 100) {
      const page = await cameraRequest("admin.vendors.list@1", { sale, limit: 100, offset }, signal);
      vendors.push(...page.items.map(item => item.id));
      if (offset + page.items.length >= page.total || !page.items.length) break;
    }
  } else {
    const realm = window.VD?.access?.realm;
    if (!realm || realm === "inglis") throw new Error("No vendor selected");
    vendors.push(realm);
  }
  const rows = {};
  let next = 0;
  let failed = false;
  // Keep the admin's sale-wide read bounded; thumbnails load independently.
  await Promise.all(Array.from({ length: Math.min(2, vendors.length) }, async () => {
    while (next < vendors.length && !signal.aborted) {
      const vendor = vendors[next++];
      try {
        const result = await cameraRequest(`${admin ? "admin" : "vendor"}.box_assignments.get@1`, { sale, vendor }, signal);
        for (const box of result.boxes) rows[`box/${box.barn.toLowerCase()}/${box.number}`] = null;
        for (const lot of result.lots) if (lot.box) {
          const key = `box/${lot.box.barn.toLowerCase()}/${lot.box.number}`;
          rows[key] = { ...lot, vendorName: result.vendorName };
        }
        if (!signal.aborted) publish({ rows: { ...rows }, loading: true, failed });
      } catch (_) { failed = true; }
    }
  }));
  if (!signal.aborted) publish({ rows, loading: false, failed });
}

function CameraTile({ camera, sale, lot, lotState, admin, playing, onPlay, onStop, activity, initialSnapshot = "", previewPaused = false, inPopup = false }) {
  const label = camera.kind === "box" ? `K Box ${camera.id.split("/").pop()}` : camera.label;
  const tile = useRef(null);
  const video = useRef(null);
  const picture = useRef(null);
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(!document.hidden);
  const visible = inView && pageVisible && !previewPaused;
  const [snapshot, setSnapshot] = useState(initialSnapshot);
  const [snapshotFailed, setSnapshotFailed] = useState(false);
  const preview = camera.preview;
  const [previewStale, setPreviewStale] = useState(false);
  const [streamFailed, setStreamFailed] = useState(false);
  const [message, setMessage] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const report = React.useMemo(() => cameraDiagnostics(camera), [camera.id, camera.kind]);
  useEffect(() => {
    if (!preview || preview.status === "unavailable") { setPreviewStale(false); return; }
    // Use elapsed time since receipt, not the device's wall clock. Recheck on
    // visibility changes as browsers can suspend timers in background tabs.
    const remaining = (preview.staleAfterSeconds - preview.ageSeconds) * 1000 - (performance.now() - preview.receivedAt);
    setPreviewStale(preview.status === "stale" || remaining < 0);
    if (remaining >= 0 && visible) {
      const timer = setTimeout(() => setPreviewStale(true), remaining + 1);
      return () => clearTimeout(timer);
    }
  }, [preview, visible]);

  useEffect(() => {
    if (!window.IntersectionObserver) { setInView(true); return; }
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.1 });
    observer.observe(tile.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const update = () => setPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);
  useEffect(() => {
    // Two selected cameras may span more than one phone screen. Scrolling must
    // not tear either down; leaving the page still stops both immediately.
    if (!pageVisible && playing) onStop(camera.id);
  }, [pageVisible, playing, onStop, camera.id]);
  useEffect(() => {
    if (!playing) { setMessage(""); setStreamFailed(false); }
    if (!playing || streamFailed) {
      setExpanded(false);
      if (document.fullscreenElement === picture.current) document.exitFullscreen?.().catch(() => {});
    }
  }, [playing, streamFailed]);
  useEffect(() => {
    if (!expanded || inPopup) return;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = overflow; };
  }, [expanded, inPopup]);

  useEffect(() => {
    if (!preview) return;
    const unavailable = preview.status === "unavailable" || !preview.url;
    setSnapshot(unavailable ? "" : preview.url); setSnapshotFailed(unavailable);
    if (!playing) setMessage(unavailable ? "Preview unavailable. Tap to try live." : "");
    if (unavailable) report("preview_unavailable");
    if (preview.status === "stale") report("preview_stale");
  }, [preview, playing]);

  useEffect(() => {
    if (!playing || !pageVisible) return;
    const controller = new AbortController();
    const media = video.current;
    let hls;
    let refreshTimer;
    let retryTimer;
    let deadlineTimer;
    let disposed = false;
    let waitingAt = null;
    const stopMedia = () => { hls?.destroy(); hls = null; media.pause(); media.removeAttribute("src"); media.load(); };
    const fail = (message) => {
      if (disposed) return;
      report("stream_failed", mediaState(media));
      clearTimeout(refreshTimer); clearTimeout(retryTimer); clearTimeout(deadlineTimer);
      stopMedia(); setStreamFailed(true); setMessage(message);
    };
    const start = async () => {
      setStreamFailed(false); setMessage("Connecting to camera…");
      media.muted = true;
      try {
        const grant = await cameraRequest("live.camera_playback@1", { sale, kind: camera.kind, camera: camera.id, media: "live" }, controller.signal);
        if (disposed) return;
        const refresh = async () => {
          try {
            const next = await cameraRequest("live.camera_playback@1", { sale, kind: camera.kind, camera: camera.id, media: "live" }, controller.signal);
            if (!disposed) refreshTimer = setTimeout(refresh, (next.expiresAt - next.issuedAt) * 800);
          } catch (error) { if (!disposed) { report("stream_renewal_error", { status: error.status }); fail(error.message); } }
        };
        refreshTimer = setTimeout(refresh, (grant.expiresAt - grant.issuedAt) * 800);
        const started = Date.now();
        const deadline = () => fail("Camera offline or busy. Please retry.");
        deadlineTimer = setTimeout(deadline, 15000);
        const ready = () => { if (waitingAt !== null) report("stream_recovered", { ...mediaState(media), stallMs: Date.now() - waitingAt }); waitingAt = null; clearTimeout(deadlineTimer); setMessage("Live"); };
        media.onplaying = ready;
        media.onwaiting = () => { waitingAt ??= Date.now(); report("stream_waiting", mediaState(media)); setMessage("Reconnecting…"); clearTimeout(deadlineTimer); deadlineTimer = setTimeout(deadline, 15000); };
        media.crossOrigin = "use-credentials";
        // Chrome can advertise native HLS while failing this gateway's stream
        // with MEDIA_ERR_SRC_NOT_SUPPORTED. Prefer the tested MSE/hls.js path;
        // use native HLS where hls.js cannot run (including older iOS Safari).
        if (!Hls.isSupported() && media.canPlayType("application/vnd.apple.mpegurl")) {
          media.src = grant.url;
          media.onerror = () => {
            report("native_error", mediaState(media));
            if (Date.now() - started < 15000) { clearTimeout(retryTimer); retryTimer = setTimeout(() => { media.load(); media.play().catch(() => {}); }, 1500); }
            else deadline();
          };
          media.play().catch(() => setMessage("Tap the picture again to start."));
        } else if (Hls.isSupported()) {
          // The gateway's low-latency parts left ~0.2s buffered and repeatedly
          // stalled in live checks. Complete segments trade a few seconds of
          // latency for enough buffer to absorb delivery jitter.
          hls = new Hls({ lowLatencyMode: false, xhrSetup: xhr => { xhr.withCredentials = true; } });
          hls.on(Hls.Events.MANIFEST_PARSED, () => media.play().catch(() => setMessage("Tap the picture again to start.")));
          hls.on(Hls.Events.ERROR, (_event, data) => {
            report("hls_error", { type: data.type, detail: data.details, fatal: data.fatal, status: data.response?.code, ...mediaState(media) });
            if (!data.fatal) return;
            if (data.response?.code === 403) { fail("Camera access expired or was denied. Please retry."); return; }
            if (Date.now() - started < 15000) { clearTimeout(retryTimer); retryTimer = setTimeout(() => { hls?.loadSource(grant.url); hls?.startLoad(); }, 1500); }
            else deadline();
          });
          hls.loadSource(grant.url);
          hls.attachMedia(media);
        } else fail("This browser cannot play this camera stream.");
      } catch (error) { if (!disposed) { report("stream_grant_error", { status: error.status }); fail(error.message); } }
    };
    start();
    const hidden = () => { if (document.hidden) onStop(camera.id); };
    document.addEventListener("visibilitychange", hidden);
    return () => {
      disposed = true; controller.abort();
      clearTimeout(refreshTimer); clearTimeout(retryTimer); clearTimeout(deadlineTimer);
      document.removeEventListener("visibilitychange", hidden);
      media.onplaying = null; media.onwaiting = null; media.onerror = null;
      stopMedia();
    };
  }, [playing, pageVisible, camera.id, camera.kind, sale, attempt, onStop]);

  async function fullscreen() {
    try {
      // Invoke directly from this gesture: fullscreen APIs require user activation.
      if (document.fullscreenEnabled && picture.current?.requestFullscreen) await picture.current.requestFullscreen();
      else if (video.current?.webkitEnterFullscreen) video.current.webkitEnterFullscreen();
      else if (video.current?.requestFullscreen) await video.current.requestFullscreen();
      else setExpanded(true);
    } catch (_) {
      // Embedded/mobile browsers can refuse native fullscreen. Keep the same
      // live element and fill the viewport; tapping it still stops playback.
      setExpanded(true);
    }
  }

  return <section ref={tile} className={`camera-tile${playing ? " camera-tile--playing" : ""}`} aria-label={label}>
    <header className="camera-heading">
      <div className="camera-title"><h3>{lot ? `Lot ${lot.number}` : label}</h3>
        {lot && <span className="camera-box-label">{label}</span>}
        {camera.feedRoom && <span className="camera-box-label">Feed room</span>}
      </div>
      {camera.kind === "box" && <p className="camera-description" title={lot ? [lot.colour, lot.sex].filter(Boolean).join(" ") : undefined}>{lot && [lot.colour, lot.sex].filter(Boolean).join(" ")}</p>}
      {camera.kind === "box" && !camera.feedRoom && <div className="camera-lot">
        {lot ? <>{lot.withdrawn && <p>Withdrawn</p>}
          <p className="camera-breeding" title={[lot.sire, lot.dam].filter(Boolean).join(" × ")} aria-label={`Sire: ${lot.sire || "not available"}. Dam: ${lot.dam || "not available"}.`}>{lot.sire && lot.dam ? `${lot.sire} × ${lot.dam}` : lot.sire ? `Sire: ${lot.sire}` : lot.dam ? `Dam: ${lot.dam}` : "Breeding not available yet"}</p>
          {admin && <p className="camera-vendor" title={lot.vendorName}>{lot.vendorName}</p>}</>
          : <span>{Object.prototype.hasOwnProperty.call(lotState.rows, camera.id) ? "No lot allocated" : lotState.loading ? "Loading lot details…" : lotState.failed ? "Lot details unavailable" : "No lot allocated"}</span>}
      </div>}
    </header>
    <button ref={picture} type="button" className={`camera-picture${expanded ? " camera-expanded" : ""}`} aria-label={`${playing ? streamFailed ? "Retry live view" : message === "Tap the picture again to start." ? "Start live view" : "Stop live view" : "Watch live"}: ${label}${lot ? `, lot ${lot.number}` : ""}`} aria-pressed={playing && !streamFailed}
      onClick={() => {
        if (!playing) onPlay(camera.id, snapshot);
        else if (streamFailed) setAttempt(n => n + 1);
        else if (message === "Tap the picture again to start.") video.current?.play().catch(() => {});
        else onStop(camera.id);
      }}>
      {playing ? <video ref={video} muted playsInline crossOrigin="use-credentials" disablePictureInPicture disableRemotePlayback/>
        : snapshot ? <img key={snapshot} src={snapshot} loading="lazy" referrerPolicy="no-referrer" alt={`${label} preview`} onError={() => {
          report("preview_image_error");
          // A presigned GET cannot be probed with HEAD. Plain img requests also
          // avoid sending CloudFront cookies or requiring S3 CORS permissions.
          setSnapshot(""); setSnapshotFailed(true); setMessage("Preview unavailable. Tap to try live.");
        }}/>
        : <span className="camera-placeholder">{!snapshotFailed ? "Loading preview…" : "Preview unavailable"}</span>}
    </button>
    {(playing || message || (snapshot && previewStale)) && <div className="camera-caption">
      {(playing || message) && <span role="status">{message || "Connecting…"}</span>}
      {!playing && snapshot && previewStale && <span className="camera-preview-stale">Stale image</span>}
      {playing && <span>{streamFailed ? "Tap picture to retry" : message === "Tap the picture again to start." ? "Tap picture to start" : "Tap picture to stop"}</span>}
      {playing && !streamFailed && <button type="button" className="camera-fullscreen" onClick={fullscreen} aria-label={`Full screen: ${label}`}>Full screen</button>}
      {inPopup && <button type="button" className="camera-fullscreen" onClick={() => onStop(camera.id)} aria-label={`Close stream: ${label}`}>Close stream</button>}
    </div>}
    {camera.kind === "parade" && <ParadeActivity camera={camera} activity={activity}/>}
  </section>;
}

function BoxCameraPopup({ camera, snapshot, sale, lotState, admin, onStop }) {
  const dialog = useRef(null);
  useEffect(() => {
    const node = dialog.current;
    const trigger = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    node.showModal();
    return () => {
      node.close();
      document.body.style.overflow = overflow;
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, []);
  return <dialog ref={dialog} className="camera-popup" aria-label="Live box camera"
    onKeyDown={event => {
      if (event.key !== "Tab" || document.fullscreenElement) return;
      const controls = [...dialog.current.querySelectorAll('button:not(:disabled)')];
      const first = controls[0], last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }}
    onCancel={event => { event.preventDefault(); onStop(); }}
    onClose={() => onStop()}
    onClick={event => { if (event.target === event.currentTarget) onStop(); }}>
    <div className="camera-popup-content">
      <header className="camera-popup-header"><h2>Live box camera</h2><button type="button" className="btn" onClick={() => onStop()}>Close live view</button></header>
      <div className="camera-popup-streams camera-grid--boxes"><CameraTile key={camera.id} camera={camera} sale={sale} lot={lotState.rows[camera.id]} lotState={lotState} admin={admin} playing onStop={onStop} initialSnapshot={snapshot} inPopup/></div>
    </div>
  </dialog>;
}

export function CameraPanel({ sale, initialKind = "parade", fixedKind = false, admin = false }) {
  const [kind, setKind] = useState(initialKind);
  const [state, setState] = useState({ data: null, error: "" });
  const activity = useParadeActivity(sale, kind === "parade", admin);
  const [attempt, setAttempt] = useState(0);
  const [session, setSession] = useState({ ids: [], posters: {}, limit: false });
  const [pageVisible, setPageVisible] = useState(!document.hidden);
  useEffect(() => {
    const update = () => setPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);
  const active = session.ids;
  const [lotState, setLotState] = useState({ rows: {}, loading: true, failed: false });
  const stop = React.useCallback(id => setSession(current => ({ ids: id ? current.ids.filter(item => item !== id) : [], posters: id ? current.posters : {}, limit: false })), []);
  const play = React.useCallback((id, snapshot = "") => setSession(current => kind === "box" ? { ids: [id], posters: { [id]: snapshot }, limit: false } : current.ids.includes(id) ? current : current.ids.length >= MAX_LIVE_STREAMS ? { ...current, limit: true } : { ids: [...current.ids, id], posters: { ...current.posters, [id]: snapshot }, limit: false }), [kind]);
  const popupCamera = kind === "box" ? state.data?.items.find(camera => camera.id === active[0]) : null;
  useEffect(() => {
    const escape = event => { if (kind !== "box" && event.key === "Escape" && !document.fullscreenElement && !document.webkitFullscreenElement) stop(); };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [stop, kind]);
  useEffect(() => {
    if (kind !== "box") return;
    const controller = new AbortController();
    setLotState({ rows: {}, loading: true, failed: false });
    cameraLots(sale, admin, controller.signal, setLotState).catch(() => {
      if (!controller.signal.aborted) setLotState({ rows: {}, loading: false, failed: true });
    });
    return () => controller.abort();
  }, [sale, kind, admin, attempt]);
  useEffect(() => {
    setState({ data: null, error: "" }); stop();
  }, [sale, kind, attempt, stop]);
  useEffect(() => {
    if (!pageVisible) return;
    const controller = new AbortController();
    let timer;
    const refresh = async () => {
      try {
        const data = await cameraRequest("live.cameras@1/previews", { sale, kind }, controller.signal);
        if (controller.signal.aborted) return;
        const receivedAt = performance.now();
        const items = data.items.map(camera => ({ ...camera, preview: { ...camera.preview, receivedAt } }));
        setState({ data: { ...data, items }, error: "" });
        // An allocation removed during renewal must also close its live tile.
        setSession(current => ({ ...current, ids: current.ids.filter(id => items.some(camera => camera.id === id)) }));
        timer = setTimeout(refresh, items.some(camera => camera.preview.status === "unavailable") ? 60000 : 240000);
      } catch (error) {
        if (controller.signal.aborted) return;
        if (error.status === 401 || error.status === 404) {
          setState({ data: null, error: error.message }); stop();
        } else {
          // Keep allocated tile labels and live players, but never call an old
          // photo current when the batch could not be re-authorized/refreshed.
          setState(current => current.data ? { error: "", data: { ...current.data,
            items: current.data.items.map(camera => ({ ...camera, preview: { status: "unavailable", url: null } }))
          } } : { data: null, error: error.message });
          timer = setTimeout(refresh, 60000);
        }
      }
    };
    refresh();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [sale, kind, attempt, pageVisible, stop]);
  return <Card className="camera-panel" title={kind === "box" ? "Stable K boxes" : "Parade areas"} subtitle="Tap a preview for live video. Previews update every five minutes.">
    {!fixedKind && <div className="row" style={{ gap: 8, marginBottom: 16 }}>
      <button className="btn" aria-pressed={kind === "parade"} onClick={() => setKind("parade")}>Parade lanes</button>
      <button className="btn" aria-pressed={kind === "box"} onClick={() => setKind("box")}>Stable K boxes</button>
    </div>}
    {kind !== "box" && active.length > 0 && <div className="camera-session"><span role="status">{active.length} of {MAX_LIVE_STREAMS} live streams</span><button type="button" className="btn" onClick={() => stop()}>Stop all</button></div>}
    {session.limit && <p role="alert">Two streams are already playing. Stop one before opening another.</p>}
    {kind === "parade" && <ActivityDate activity={activity}/>}
    {state.error ? <p role="alert">{state.error} <button className="btn" onClick={() => setAttempt(n => n + 1)}>Retry cameras</button></p> : !state.data ? <p role="status">Loading cameras…</p> : !state.data.playbackEnabled ? <p role="status">Camera playback is not configured.</p> : !state.data.items.length ? <p role="status">No cameras are allocated to this vendor for this sale.</p> : <div className={`camera-grid${kind === "box" ? " camera-grid--boxes" : ""}`}>{state.data.items.map(camera => <CameraTile key={`${sale}:${camera.id}`} camera={camera} sale={sale} lot={lotState.rows[camera.id]} lotState={lotState} admin={admin} playing={kind !== "box" && active.includes(camera.id)} previewPaused={kind === "box" && active.length > 0} onPlay={play} onStop={stop} activity={activity}/>)}</div>}
    {popupCamera && <BoxCameraPopup camera={popupCamera} snapshot={session.posters[popupCamera.id]} sale={sale} lotState={lotState} admin={admin} onStop={stop}/>}
  </Card>;
}

export function VendorCameras({ sale }) {
  const [kind, setKind] = useState(null);
  const [hasBoth, setHasBoth] = useState(false);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setKind(null); setHasBoth(false); setError("");
    Promise.all(["box", "parade"].map(kind => cameraRequest("live.cameras@1", { sale, kind }, controller.signal)))
      .then(([boxes, parade]) => { if (!controller.signal.aborted) { setKind(boxes.items.length ? "box" : "parade"); setHasBoth(Boolean(boxes.items.length && parade.items.length)); } })
      .catch(error => { if (!controller.signal.aborted) setError(error.message); });
    return () => controller.abort();
  }, [sale, attempt]);
  return error ? <p role="alert">{error} <button className="btn" onClick={() => setAttempt(n => n + 1)}>Retry cameras</button></p> : kind ? <>{hasBoth && <nav className="camera-tabs" aria-label="Your camera views"><button className="btn" aria-pressed={kind === "box"} onClick={() => setKind("box")}>Stable K boxes</button><button className="btn" aria-pressed={kind === "parade"} onClick={() => setKind("parade")}>Parade areas</button></nav>}<CameraPanel key={`${sale}:${kind}`} sale={sale} initialKind={kind} fixedKind/></> : <p role="status">Loading your cameras…</p>;
}

export function AdminCameras({ kind = "parade" }) {
  const [sales, setSales] = useState([]);
  const [sale, setSale] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    cameraRequest("admin.sales.list@1", { limit: 100, offset: 0 }, controller.signal).then(result => {
      if (controller.signal.aborted) return;
      const live = result.items.filter(item => item.lifecycle === "LIVE");
      setSales(live); setSale(live[0]?.id || "");
    }).catch(error => { if (!controller.signal.aborted) setError(error.message); });
    return () => controller.abort();
  }, []);
  return <div><label className="field">Camera sale<select value={sale} onChange={event => setSale(event.target.value)}><option value="">Select a LIVE sale</option>{sales.map(item => <option key={item.id}>{item.id}</option>)}</select></label>{error && <p role="alert">{error}</p>}{sale && <CameraPanel key={`${sale}:${kind}`} sale={sale} initialKind={kind} fixedKind admin/>}</div>;
}
